<template>
  <v-menu
    v-model="menuOpen"
    :close-on-content-click="false"
    location="bottom start"
    offset="6"
    :disabled="disabled"
  >
    <template #activator="{ props: menuProps }">
      <v-text-field
        v-bind="menuProps"
        :model-value="displayValue"
        readonly
        placeholder="--:-- --"
        variant="outlined"
        density="default"
        hide-details="auto"
        class="tk-dialog__input wh-time-field"
        append-inner-icon="mdi-clock-outline"
        :rules="fieldRules"
        :disabled="disabled"
        :error="Boolean(error)"
      />
    </template>

    <div class="wh-time-picker" role="listbox" aria-label="Pick time">
      <div class="wh-time-picker__col" aria-label="Hour">
        <button
          v-for="hour in hours"
          :key="`h-${hour}`"
          type="button"
          class="wh-time-picker__item"
          :class="{ 'wh-time-picker__item--active': selected.hour === hour }"
          :ref="(el) => setItemRef('hour', hour, el)"
          @click="selectHour(hour)"
        >
          {{ pad(hour) }}
        </button>
      </div>
      <div class="wh-time-picker__col" aria-label="Minute">
        <button
          v-for="minute in minutes"
          :key="`m-${minute}`"
          type="button"
          class="wh-time-picker__item"
          :class="{ 'wh-time-picker__item--active': selected.minute === minute }"
          :ref="(el) => setItemRef('minute', minute, el)"
          @click="selectMinute(minute)"
        >
          {{ pad(minute) }}
        </button>
      </div>
      <div class="wh-time-picker__col wh-time-picker__col--period" aria-label="AM or PM">
        <button
          v-for="period in periods"
          :key="period"
          type="button"
          class="wh-time-picker__item"
          :class="{ 'wh-time-picker__item--active': selected.period === period }"
          @click="selectPeriod(period)"
        >
          {{ period }}
        </button>
      </div>
    </div>
  </v-menu>
</template>

<script setup>
import { computed, nextTick, reactive, ref, watch } from 'vue'

defineOptions({ name: 'WorkingHoursTimeField' })

const props = defineProps({
  modelValue: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  error: { type: Boolean, default: false },
  /** Rules receive the 24-hour HH:MM model value. */
  rules: { type: Array, default: () => [] },
})

const emit = defineEmits(['update:modelValue'])

const hours = Array.from({ length: 12 }, (_, i) => i + 1)
const minutes = Array.from({ length: 60 }, (_, i) => i)
const periods = ['AM', 'PM']

const menuOpen = ref(false)
const selected = reactive({
  hour: null,
  minute: null,
  period: null,
})
const itemRefs = {
  hour: {},
  minute: {},
}

const fieldRules = computed(() =>
  (Array.isArray(props.rules) ? props.rules : []).map(
    (rule) => () =>
      typeof rule === 'function' ? rule(props.modelValue) : rule,
  ),
)

const displayValue = computed(() => {
  const parts = parseHhMm(props.modelValue)
  if (!parts) return ''
  return `${parts.hour}:${pad(parts.minute)} ${parts.period}`
})

watch(
  () => props.modelValue,
  (value) => {
    const parts = parseHhMm(value)
    selected.hour = parts?.hour ?? null
    selected.minute = parts?.minute ?? null
    selected.period = parts?.period ?? null
  },
  { immediate: true },
)

watch(menuOpen, async (open) => {
  if (!open) return
  await nextTick()
  scrollActiveIntoView('hour', selected.hour)
  scrollActiveIntoView('minute', selected.minute)
})

function pad(value) {
  return String(value).padStart(2, '0')
}

function parseHhMm(value) {
  const raw = String(value ?? '').trim()
  const match = raw.match(/^([01]?\d|2[0-3]):([0-5]\d)(?::[0-5]\d)?$/)
  if (!match) return null
  const hour24 = Number(match[1])
  const minute = Number(match[2])
  const period = hour24 >= 12 ? 'PM' : 'AM'
  let hour = hour24 % 12
  if (hour === 0) hour = 12
  return { hour, minute, period }
}

function toHhMm(hour, minute, period) {
  let hour24 = Number(hour) % 12
  if (period === 'PM') hour24 += 12
  return `${pad(hour24)}:${pad(minute)}`
}

function emitValue() {
  if (selected.hour == null || selected.minute == null || !selected.period) {
    return
  }
  emit(
    'update:modelValue',
    toHhMm(selected.hour, selected.minute, selected.period),
  )
}

function ensureDefaults() {
  if (selected.hour == null) selected.hour = 9
  if (selected.minute == null) selected.minute = 0
  if (!selected.period) selected.period = 'AM'
}

function selectHour(hour) {
  ensureDefaults()
  selected.hour = hour
  emitValue()
}

function selectMinute(minute) {
  ensureDefaults()
  selected.minute = minute
  emitValue()
}

function selectPeriod(period) {
  ensureDefaults()
  selected.period = period
  emitValue()
}

function setItemRef(kind, value, el) {
  if (el) itemRefs[kind][value] = el
}

function scrollActiveIntoView(kind, value) {
  if (value == null) return
  const el = itemRefs[kind][value]
  if (el && typeof el.scrollIntoView === 'function') {
    el.scrollIntoView({ block: 'center' })
  }
}
</script>

<style scoped>
.wh-time-picker {
  display: grid;
  grid-template-columns: 4.25rem 4.25rem 4.25rem;
  gap: 0;
  max-height: 14rem;
  overflow: hidden;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.14);
}

.wh-time-picker__col {
  display: flex;
  flex-direction: column;
  max-height: 14rem;
  overflow-y: auto;
  overscroll-behavior: contain;
  border-right: 1px solid #e2e8f0;
  scrollbar-width: thin;
}

.wh-time-picker__col:last-child,
.wh-time-picker__col--period {
  border-right: none;
}

.wh-time-picker__col--period {
  justify-content: flex-start;
}

.wh-time-picker__item {
  appearance: none;
  border: 0;
  background: transparent;
  margin: 0;
  padding: 0.55rem 0.35rem;
  font: inherit;
  font-size: 0.92rem;
  font-variant-numeric: tabular-nums;
  color: #0f172a;
  text-align: center;
  cursor: pointer;
  line-height: 1.2;
}

.wh-time-picker__item:hover {
  background: #f1f5f9;
}

.wh-time-picker__item--active {
  background: #2563eb;
  color: #fff;
  font-weight: 650;
}

.wh-time-picker__item--active:hover {
  background: #1d4ed8;
  color: #fff;
}

:deep(.wh-time-field .v-field) {
  cursor: pointer;
}

:deep(.wh-time-field input) {
  cursor: pointer;
}
</style>
